import { apiClient } from './client'
import { ApiError } from './errors'

export interface Product {
  id: number
  name: string
  description: string
  sku: string
  tradable: boolean
  unitType: string
  // GET /products: 'metal' or 'art' (the C.F. Goldie painting). Absent on an
  // older backend and on the performance summaries' product.
  kind?: string
  // GET /products: the units one whole item is split into. The painting is
  // 100,000,000 units, one unit a hundred-millionth of it.
  quantityPerItem?: number
}

export interface SpotPrice {
  id: number
  midpointPerMg: number
  askPricePerMg: number
  bidPricePerMg: number
  spotTime: string
}

export interface HistoricalSpotPrice {
  id: number
  midpointPerG: number
  spotDate: string
}

export interface ProductPerformance {
  currentValue: number
  netProfit: number
  quantityOwned: number
  periodInvestedInDays: number
  totalCost: number
  totalReturn: number
  totalReturnAsPercentage: number
  unit: string
  spotPrice: SpotPrice
}

type ProductPerformanceResponse = Partial<ProductPerformance> & {
  current_value?: number | string
  total_quantity_mg?: number | string
  cost_basis?: number | string
  gain_loss?: number | string
  gain_loss_pct?: number | string | null
}

function mapProductPerformance(
  data: ProductPerformanceResponse
): ProductPerformance {
  const currentValue = Number(data.currentValue ?? data.current_value ?? 0)
  const totalCost = Number(data.totalCost ?? data.cost_basis ?? 0)
  const totalReturn = Number(data.totalReturn ?? data.gain_loss ?? currentValue)

  return {
    currentValue,
    netProfit: Number(data.netProfit ?? totalReturn),
    quantityOwned: Number(data.quantityOwned ?? data.total_quantity_mg ?? 0),
    periodInvestedInDays: Number(data.periodInvestedInDays ?? 0),
    totalCost,
    totalReturn,
    totalReturnAsPercentage: Number(
      data.totalReturnAsPercentage ?? data.gain_loss_pct ?? 0
    ),
    unit: data.unit ?? 'mg',
    spotPrice: data.spotPrice ?? {
      id: 0,
      midpointPerMg: 0,
      askPricePerMg: 0,
      bidPricePerMg: 0,
      spotTime: ''
    }
  }
}

export interface ProductPerformanceSummary {
  currentValue: number
  totalCost: number
  totalReturn: number
  totalReturnAsPercentage: number
  product: Product
}

export interface SecurityOwned {
  id: number
  quantityMg: number
  security: {
    id: number
    serialNumber: string
    vault: { id: number; name: string }
  }
}

export interface TcAcceptance {
  productId: number
  accepted: boolean
}

export interface Vault {
  id: number
  name: string
  email?: string
  dateOfBirth?: string
  relationship?: string
  balance?: number
  isDefault?: boolean
}

type VaultResponse = Vault & {
  date_of_birth?: string
  is_default?: boolean
}

function mapVault(vault: VaultResponse): Vault {
  return {
    ...vault,
    dateOfBirth: vault.dateOfBirth ?? vault.date_of_birth,
    isDefault: vault.isDefault ?? vault.is_default
  }
}

// What a create or an update writes. A field left undefined is not sent and
// stays as it is; on an update, null (or a blank email) clears the child's
// email or date of birth (goldie-cloud PUT /vaults/{id}).
export interface VaultWrite {
  name?: string
  email?: string | null
  dateOfBirth?: string | null
  relationship?: string
}

function toVaultRequest(data: VaultWrite) {
  return {
    name: data.name,
    email: data.email,
    date_of_birth: data.dateOfBirth,
    relationship: data.relationship
  }
}

const filled = (value: unknown): boolean =>
  typeof value === 'string' && value.trim() !== ''

// Whether goldie-cloud treats a vault as a Kids Vault
// (services/vault_archive_rules.py has_kids_details): not the customer's main
// vault, with a child's name, relationship or date of birth. The vault made at
// sign-up has none of them. Takes a vault as /user/me sends it, in either case.
export function isKidsVault(vault: Record<string, unknown>): boolean {
  if (vault.isDefault === true || vault.is_default === true) return false
  return (
    filled(vault.name) ||
    filled(vault.relationship) ||
    filled(vault.dateOfBirth) ||
    filled(vault.date_of_birth)
  )
}

export interface MarketOrder {
  id: number
  createdAt: string
  orderType: string
  quantityMg: number
  pricePerUnit: number
}

type ProductResponse = Partial<Product> & {
  unit_type?: string
  quantity_per_item?: number | string | null
}

function mapProduct(p: ProductResponse): Product {
  const quantityPerItem = Number(p.quantityPerItem ?? p.quantity_per_item)
  return {
    id: p.id as number,
    name: p.name as string,
    description: p.description ?? '',
    sku: p.sku as string,
    tradable: p.tradable ?? false,
    // BE /products returns unit_type (snake); the app reads unitType everywhere.
    unitType: p.unitType ?? p.unit_type ?? 'mg',
    kind: p.kind ?? undefined,
    quantityPerItem: quantityPerItem > 0 ? quantityPerItem : undefined
  }
}

export async function fetchProducts(): Promise<Product[]> {
  try {
    const response = await apiClient.get<ProductResponse[]>('products')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const body = response.data as any
    const list = (body?.data ?? body) as ProductResponse[]
    return (list ?? []).map(mapProduct)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchSpotPrice(productId: number): Promise<SpotPrice> {
  try {
    const response = await apiClient.get<SpotPrice>(
      `spot_price/latest/${productId}`
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchHistoricalSpotPrices(
  productId: number,
  timeframe: string
): Promise<HistoricalSpotPrice[]> {
  try {
    const response = await apiClient.get<HistoricalSpotPrice[]>(
      `spot_price/historical/${productId}`,
      { params: { timeframe } }
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

function getVaultParams(vaultId?: number | null) {
  return vaultId != null && Number.isFinite(vaultId)
    ? { vault_id: vaultId }
    : undefined
}

export async function fetchProductPerformance(
  productId: number,
  vaultId?: number | null
): Promise<ProductPerformance> {
  try {
    const response = await apiClient.get<ProductPerformanceResponse>(
      `products/${productId}/performance`,
      { params: getVaultParams(vaultId) }
    )
    return mapProductPerformance(response.data)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchPerformanceSummaries(
  vaultId?: number | null
): Promise<ProductPerformanceSummary[]> {
  try {
    const response = await apiClient.get<ProductPerformanceSummary[]>(
      'products/performance_summaries',
      { params: getVaultParams(vaultId) }
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchSecuritiesOwned(
  productId: number
): Promise<SecurityOwned[]> {
  try {
    const response = await apiClient.get<SecurityOwned[]>('securities/owned', {
      params: { product_id: productId }
    })
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchTcAcceptance(
  productId: number
): Promise<TcAcceptance> {
  try {
    const response = await apiClient.get<TcAcceptance>(
      `products/${productId}/tc_acceptance`
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function acceptTcAcceptance(productId: number): Promise<void> {
  try {
    await apiClient.post(`products/${productId}/tc_acceptance`)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchVault(vaultId: number): Promise<Vault> {
  try {
    const response = await apiClient.get<VaultResponse>(`vaults/${vaultId}`)
    return mapVault(response.data)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function createVault(data: {
  name: string
  email?: string
  dateOfBirth?: string
  relationship?: string
}): Promise<Vault> {
  try {
    const response = await apiClient.post<VaultResponse>(
      'vaults',
      toVaultRequest(data)
    )
    return mapVault(response.data)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function updateVault(
  vaultId: number,
  data: VaultWrite
): Promise<Vault> {
  try {
    const response = await apiClient.put<VaultResponse>(
      `vaults/${vaultId}`,
      toVaultRequest(data)
    )
    return mapVault(response.data)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function activateVault(vaultId: number): Promise<Vault> {
  try {
    const response = await apiClient.post<VaultResponse>(
      `vaults/${vaultId}/activate`
    )
    return mapVault(response.data)
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

// ---- Removing an empty Kids Vault (the kids_vault_self_remove flag) ----

// One thing in the way of removing a vault, in the backend's own words for the
// customer ("This vault holds 2.345 g of gold. Sell it first, or contact us."),
// with its code (HOLDINGS, AUTO_INVEST_PLAN, FEATURE_OFF ...).
export interface VaultRemoveBlocker {
  code: string
  message: string
}

export interface VaultRemoveCheck {
  eligible: boolean
  blockers: VaultRemoveBlocker[]
}

export type VaultRemoveRefusal =
  // Something is in the way now (a 409, which names what).
  | { kind: 'blocked'; blockers: VaultRemoveBlocker[] }
  // The vault isn't there any more (a 404): already removed.
  | { kind: 'gone' }
  | { kind: 'failed' }

function toBlockers(raw: unknown): VaultRemoveBlocker[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map(b => ({
      code: String((b as { code?: unknown } | null)?.code ?? ''),
      message: String((b as { message?: unknown } | null)?.message ?? '').trim()
    }))
    .filter(b => b.message.length > 0)
}

// The remove routes refuse with FastAPI's {"detail": {status, message, data}}
// (404 not found, 409 with data.blockers), which ApiError.fromAxiosError would
// lose: keep its words, code and data.
function removalError(err: unknown): ApiError {
  const response = (
    err as {
      isAxiosError?: boolean
      response?: { status: number; data?: unknown }
    }
  )?.response
  const detail = (response?.data as { detail?: unknown } | undefined)?.detail
  if (response && detail && typeof detail === 'object') {
    const d = detail as { message?: string; data?: { code?: string } }
    return new ApiError(
      d.message ?? 'Request failed',
      d.data?.code ?? `HTTP_${response.status}`,
      response.status,
      d.data
    )
  }
  return ApiError.fromAxiosError(err)
}

// GET /vaults/{id}/remove-check: can the customer remove this vault now? Only
// an empty Kids Vault with nothing pending can go.
export async function fetchVaultRemoveCheck(
  vaultId: number
): Promise<VaultRemoveCheck> {
  try {
    const response = await apiClient.get<Partial<VaultRemoveCheck>>(
      `vaults/${vaultId}/remove-check`
    )
    const blockers = toBlockers(response.data?.blockers)
    return {
      eligible: response.data?.eligible === true && blockers.length === 0,
      blockers
    }
  } catch (err) {
    throw removalError(err)
  }
}

// POST /vaults/{id}/remove: the backend archives the vault (hidden, every record
// kept), stops it being the vault the customer views, and emails the account
// holder. A refusal throws: read it with vaultRemoveRefusal.
export async function removeVault(vaultId: number): Promise<void> {
  try {
    await apiClient.post(`vaults/${vaultId}/remove`)
  } catch (err) {
    throw removalError(err)
  }
}

export function vaultRemoveRefusal(err: unknown): VaultRemoveRefusal {
  if (!(err instanceof ApiError)) return { kind: 'failed' }
  const blockers = toBlockers(
    (err.details as { blockers?: unknown } | undefined)?.blockers
  )
  if (err.status === 409 || blockers.length > 0) {
    return {
      kind: 'blocked',
      blockers:
        blockers.length || !err.message
          ? blockers
          : [{ code: err.code, message: err.message }]
    }
  }
  if (err.status === 404) return { kind: 'gone' }
  return { kind: 'failed' }
}

type MarketOrderResponse = Partial<MarketOrder> & {
  created_at?: string
  order_type?: string
  // BE MarketOrder.amount is the quantity in mg; price_per_unit is the unit price.
  amount?: number
  quantity_mg?: number
  price_per_unit?: number | string
}

function mapMarketOrder(o: MarketOrderResponse): MarketOrder {
  return {
    id: o.id as number,
    createdAt: o.createdAt ?? o.created_at ?? '',
    orderType: o.orderType ?? o.order_type ?? '',
    quantityMg: Number(o.quantityMg ?? o.quantity_mg ?? o.amount ?? 0),
    pricePerUnit: Number(o.pricePerUnit ?? o.price_per_unit ?? 0)
  }
}

export async function fetchMarketOrders(
  productId: number
): Promise<MarketOrder[]> {
  try {
    // BE /market_orders/{product_id} returns rows in snake_case
    // (created_at/order_type/amount/price_per_unit). Map to the camelCase the
    // TableTransactionHistory rows read (createdAt/orderType/quantityMg/pricePerUnit).
    const response = await apiClient.get<MarketOrderResponse[]>(
      `market_orders/${productId}`
    )
    const data = response.data
    return Array.isArray(data) ? data.map(mapMarketOrder) : []
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}
